import { useEffect, useMemo, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { ArrowLeft, ChevronDown, ChevronRight, Plus, Save, Trash2, Upload } from "lucide-react";
import { toast } from "sonner";
import { adminPages, adminUploads, formatError } from "../api";
import { Button, Card, Input, PageHeader, Spinner, Textarea } from "../ui";

export default function PageEditor() {
  const { slug } = useParams();
  const [page, setPage] = useState(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    adminPages.get(slug).then(setPage).catch(() => setPage(false));
  }, [slug]);

  if (page === false) return <p className="text-sm text-red-600">Page not found.</p>;
  if (!page) return <Spinner />;

  const saveSeo = async () => {
    setBusy(true);
    try {
      await adminPages.patchSeo(slug, {
        meta_title: page.meta_title,
        meta_description: page.meta_description,
        og_image: page.og_image,
      });
      toast.success("SEO saved.");
    } catch (err) {
      toast.error(formatError(err));
    } finally {
      setBusy(false);
    }
  };

  const saveSection = async (sectionKey, patch) => {
    try {
      const updated = await adminPages.patchSection(slug, sectionKey, patch);
      setPage((p) => ({
        ...p,
        sections: p.sections.map((s) => (s.key === sectionKey ? updated : s)),
      }));
      toast.success("Section saved.");
    } catch (err) {
      toast.error(formatError(err));
    }
  };

  return (
    <div data-testid={`admin-page-editor-${slug}`}>
      <Link to="/admin/pages" className="inline-flex items-center gap-1 text-xs font-semibold uppercase tracking-[0.12em] text-[hsl(var(--aoa-royal))] mb-4">
        <ArrowLeft className="h-3.5 w-3.5" /> All pages
      </Link>
      <PageHeader
        title={page.title}
        description={<>Route: <code className="rounded bg-slate-100 px-1.5 py-0.5 text-xs">{page.route}</code></>}
      />

      <Card className="p-6 mb-6" testId="seo-card">
        <h2 className="font-display text-lg font-semibold text-[hsl(var(--aoa-navy))] mb-4">SEO</h2>
        <div className="grid gap-4 md:grid-cols-2">
          <Input label="Meta title" value={page.meta_title || ""} onChange={(e) => setPage({ ...page, meta_title: e.target.value })} testId="seo-meta-title" />
          <Input label="OG image path" value={page.og_image || ""} onChange={(e) => setPage({ ...page, og_image: e.target.value })} testId="seo-og-image" hint="Path under /imagery/* or /api/files/*" />
        </div>
        <div className="mt-4">
          <Textarea label="Meta description" rows={3} value={page.meta_description || ""} onChange={(e) => setPage({ ...page, meta_description: e.target.value })} testId="seo-meta-description" />
        </div>
        <div className="mt-5 flex justify-end">
          <Button onClick={saveSeo} loading={busy} testId="admin-page-save"><Save className="h-3.5 w-3.5" />Save SEO</Button>
        </div>
      </Card>

      <h2 className="font-display text-lg font-semibold text-[hsl(var(--aoa-navy))] mb-3">Sections</h2>
      <div className="space-y-3">
        {(page.sections || []).map((s, i) => (
          <SectionEditor key={s.key} index={i} section={s} onSave={(patch) => saveSection(s.key, patch)} />
        ))}
      </div>
    </div>
  );
}

function SectionEditor({ section, index, onSave }) {
  const [open, setOpen] = useState(index === 0);
  const [draft, setDraft] = useState(section);
  const [busy, setBusy] = useState(false);
  const has = (k) => Object.prototype.hasOwnProperty.call(section, k);

  const upload = async (purpose, file) => {
    const r = await adminUploads.upload(file, purpose);
    return r.url;
  };

  const save = async () => {
    setBusy(true);
    try {
      const patch = {};
      ["heading", "body", "image", "image_alt", "items", "ctas"].forEach((k) => {
        if (has(k)) patch[k] = draft[k];
      });
      await onSave(patch);
    } finally {
      setBusy(false);
    }
  };

  return (
    <Card testId={`section-card-${section.key}`}>
      <button
        onClick={() => setOpen(!open)}
        data-testid={`section-toggle-${section.key}`}
        className="w-full flex items-center justify-between px-5 py-4 text-left"
      >
        <div className="flex items-center gap-3 min-w-0">
          <span className="grid h-7 w-7 place-items-center rounded-md bg-slate-100 text-xs font-bold text-[hsl(var(--aoa-navy))]">{String(index + 1).padStart(2, "0")}</span>
          <div className="min-w-0">
            <div className="font-semibold text-[hsl(var(--aoa-navy))] truncate">{draft.heading || section.key}</div>
            <div className="text-xs text-slate-500">type: <code className="rounded bg-slate-100 px-1.5 py-0.5">{section.type}</code> · key: <code className="rounded bg-slate-100 px-1.5 py-0.5">{section.key}</code></div>
          </div>
        </div>
        {open ? <ChevronDown className="h-4 w-4 text-slate-500 flex-none" /> : <ChevronRight className="h-4 w-4 text-slate-500 flex-none" />}
      </button>

      {open && (
        <div className="border-t border-slate-100 px-5 pb-5 pt-4 space-y-4">
          {has("heading") && (
            <Input label="Heading" value={draft.heading || ""} onChange={(e) => setDraft({ ...draft, heading: e.target.value })} testId={`section-heading-${section.key}`} />
          )}
          {has("body") && (
            <Textarea label="Body" rows={4} value={draft.body || ""} onChange={(e) => setDraft({ ...draft, body: e.target.value })} testId={`section-body-${section.key}`} />
          )}
          {has("image") && (
            <ImagePicker label="Image" value={draft.image} alt={draft.image_alt} onChange={(v, a) => setDraft({ ...draft, image: v, ...(a !== undefined ? { image_alt: a } : {}) })} onUpload={async (f) => upload("image", f)} testKey={section.key} />
          )}
          {has("items") && Array.isArray(draft.items) && (
            <ItemsEditor items={draft.items} onChange={(items) => setDraft({ ...draft, items })} sectionKey={section.key} />
          )}
          {has("ctas") && Array.isArray(draft.ctas) && (
            <CTAsEditor ctas={draft.ctas} onChange={(ctas) => setDraft({ ...draft, ctas })} sectionKey={section.key} />
          )}
          <div className="flex justify-end">
            <Button onClick={save} loading={busy} testId={`save-section-${section.key}`}><Save className="h-3.5 w-3.5" /> Save section</Button>
          </div>
        </div>
      )}
    </Card>
  );
}

function ImagePicker({ label, value, alt, onChange, onUpload, testKey }) {
  const [busy, setBusy] = useState(false);
  const handleFile = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    setBusy(true);
    try {
      const url = await onUpload(f);
      onChange(url, alt);
      toast.success("Image uploaded.");
    } catch (err) {
      toast.error(formatError(err));
    } finally {
      setBusy(false);
      e.target.value = "";
    }
  };
  return (
    <div>
      <span className="mb-1.5 inline-block text-xs font-semibold uppercase tracking-[0.12em] text-slate-700">{label}</span>
      <div className="flex flex-col md:flex-row gap-3 items-start">
        {value && (
          <img src={value} alt={alt || ""} className="h-24 w-32 object-cover rounded-lg border border-slate-200" />
        )}
        <div className="flex-1 space-y-2 min-w-0">
          <input
            type="text"
            value={value || ""}
            onChange={(e) => onChange(e.target.value, alt)}
            data-testid={`section-image-url-${testKey}`}
            placeholder="/imagery/... or /api/files/image/..."
            className="block w-full rounded-lg border border-slate-200 px-3 py-2 text-sm"
          />
          {alt !== undefined && (
            <input
              type="text"
              value={alt || ""}
              onChange={(e) => onChange(value, e.target.value)}
              data-testid={`section-image-alt-${testKey}`}
              placeholder="Alt text (for accessibility)"
              className="block w-full rounded-lg border border-slate-200 px-3 py-2 text-sm"
            />
          )}
          <label className="inline-flex items-center gap-2 rounded-lg border border-dashed border-slate-300 px-3 py-2 text-xs font-semibold text-slate-700 cursor-pointer hover:border-[hsl(var(--aoa-royal))]">
            <Upload className="h-3.5 w-3.5" />
            {busy ? "Uploading..." : "Replace image"}
            <input type="file" hidden accept="image/*" onChange={handleFile} data-testid={`section-image-file-${testKey}`} />
          </label>
        </div>
      </div>
    </div>
  );
}

function ItemsEditor({ items, onChange, sectionKey }) {
  // Items can be strings (chip_grid) or objects (varies).
  const isStringList = items.length === 0 || typeof items[0] === "string";
  return (
    <div>
      <span className="mb-1.5 inline-block text-xs font-semibold uppercase tracking-[0.12em] text-slate-700">Items ({items.length})</span>
      <div className="space-y-2">
        {items.map((it, idx) => (
          <div key={idx} className="flex gap-2 items-start" data-testid={`item-row-${sectionKey}-${idx}`}>
            <span className="mt-2 text-[11px] text-slate-400 tabular-nums w-6">{String(idx + 1).padStart(2, "0")}</span>
            <div className="flex-1 min-w-0">
              {isStringList ? (
                <input
                  type="text"
                  value={it}
                  onChange={(e) => {
                    const next = [...items];
                    next[idx] = e.target.value;
                    onChange(next);
                  }}
                  className="block w-full rounded-lg border border-slate-200 px-3 py-2 text-sm"
                />
              ) : (
                <ObjectItemEditor
                  obj={it}
                  onChange={(o) => {
                    const next = [...items];
                    next[idx] = o;
                    onChange(next);
                  }}
                />
              )}
            </div>
            <button
              type="button"
              onClick={() => onChange(items.filter((_, i) => i !== idx))}
              className="mt-1 text-slate-400 hover:text-red-600"
              data-testid={`item-remove-${sectionKey}-${idx}`}
              title="Remove"
            >
              <Trash2 className="h-4 w-4" />
            </button>
          </div>
        ))}
      </div>
      <button
        type="button"
        onClick={() =>
          onChange([
            ...items,
            isStringList ? "" : Object.fromEntries(Object.keys(items[0] || { title: "", body: "" }).map((k) => [k, ""])),
          ])
        }
        data-testid={`item-add-${sectionKey}`}
        className="mt-2 inline-flex items-center gap-1.5 rounded-lg border border-dashed border-slate-300 px-3 py-1.5 text-xs font-semibold text-slate-700 hover:border-[hsl(var(--aoa-royal))]"
      >
        <Plus className="h-3.5 w-3.5" /> Add item
      </button>
    </div>
  );
}

function ObjectItemEditor({ obj, onChange }) {
  return (
    <div className="grid gap-2 sm:grid-cols-2">
      {Object.entries(obj).map(([k, v]) => (
        <label key={k} className="block">
          <span className="text-[10px] font-semibold uppercase tracking-[0.12em] text-slate-500">{k}</span>
          <input
            type="text"
            value={v ?? ""}
            onChange={(e) => onChange({ ...obj, [k]: e.target.value })}
            className="mt-0.5 block w-full rounded-lg border border-slate-200 px-2.5 py-1.5 text-sm"
          />
        </label>
      ))}
    </div>
  );
}

function CTAsEditor({ ctas, onChange, sectionKey }) {
  return (
    <div>
      <span className="mb-1.5 inline-block text-xs font-semibold uppercase tracking-[0.12em] text-slate-700">Call-to-action buttons</span>
      <div className="space-y-2">
        {ctas.map((c, i) => (
          <div key={i} className="grid gap-2 sm:grid-cols-[1fr_1fr_120px_auto]" data-testid={`cta-row-${sectionKey}-${i}`}>
            <input type="text" value={c.label || ""} onChange={(e) => { const n = [...ctas]; n[i] = { ...c, label: e.target.value }; onChange(n); }} placeholder="Label" className="rounded-lg border border-slate-200 px-3 py-2 text-sm" />
            <input type="text" value={c.href || ""} onChange={(e) => { const n = [...ctas]; n[i] = { ...c, href: e.target.value }; onChange(n); }} placeholder="/route or /api/..." className="rounded-lg border border-slate-200 px-3 py-2 text-sm" />
            <select value={c.variant || "primary"} onChange={(e) => { const n = [...ctas]; n[i] = { ...c, variant: e.target.value }; onChange(n); }} className="rounded-lg border border-slate-200 px-3 py-2 text-sm">
              <option value="primary">primary</option>
              <option value="ghost">ghost</option>
            </select>
            <button type="button" onClick={() => onChange(ctas.filter((_, j) => j !== i))} className="text-slate-400 hover:text-red-600"><Trash2 className="h-4 w-4" /></button>
          </div>
        ))}
      </div>
      <button type="button" onClick={() => onChange([...ctas, { label: "", href: "", variant: "primary" }])} className="mt-2 inline-flex items-center gap-1.5 rounded-lg border border-dashed border-slate-300 px-3 py-1.5 text-xs font-semibold text-slate-700 hover:border-[hsl(var(--aoa-royal))]"><Plus className="h-3.5 w-3.5" /> Add CTA</button>
    </div>
  );
}
